import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import pdfjsPackage from 'pdfjs-dist/package.json';

const PDF_WORKER_URL = `https://unpkg.com/pdfjs-dist@${pdfjsPackage.version}/build/pdf.worker.min.js`;

// =====================================================================
//  MARKDOWN STRIPPING
// =====================================================================

/**
 * Strip all Markdown formatting so the text can be matched against
 * the plain text extracted from a PDF.
 *
 * Order matters: block-level constructs first, then inline.
 */
const stripMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[=\-]{2,}\s*$/gm, '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, ' $1 ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\[[^\]]*\]/g, '$1')
    .replace(/(\*{3}|_{3})(.*?)\1/g, '$2')
    .replace(/(\*{2}|_{2})(.*?)\1/g, '$2')
    .replace(/([*_])(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/^>\s?/gm, '')
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^[\s]*\d+\.\s+/gm, '')
    .replace(/^[-*_]{3,}\s*$/gm, '')
    .replace(/\|/g, ' ')
    .replace(/^[\s:|-]+$/gm, '')
    .replace(/^\[[^\]]+\]:\s*\S+.*$/gm, '')
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

// =====================================================================
//  TEXT NORMALIZATION
// =====================================================================

const normalizeForMatch = (text) => {
  if (!text) return '';
  return text
    .replace(/[*_~`#>|\\]/g, ' ')
    .replace(/<!--.*?-->/gs, ' ')
    .replace(/!\[.*?\]\(.*?\)/g, ' ')
    .replace(/\[.*?\]\(.*?\)/g, ' ')
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/[\u00AD\u200B\u200C\u200D\uFEFF]/g, '')
    .replace(/\uFB01/g, 'fi').replace(/\uFB02/g, 'fl')
    .replace(/\uFB00/g, 'ff').replace(/\uFB03/g, 'ffi').replace(/\uFB04/g, 'ffl')
    .replace(/[\u2018\u2019\u201A\u201B`]/g, "'")
    .replace(/[\u201C\u201D\u201E\u201F]/g, '"')
    .replace(/[\u2013\u2014\u2015\u2212]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[^\S ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
};

const cleanText = (text) => {
  if (!text) return '';
  const stripped = stripMarkdown(text);
  return stripped.replace(/\s+/g, ' ').trim();
};

// =====================================================================
//  PDF TEXT EXTRACTION
// =====================================================================

const extractPdfText = async (pdfDoc) => {
  const numPages = pdfDoc.numPages;
  const pages = [];
  const concatParts = [];
  const indexMap = [];
  let charOffset = 0;

  for (let i = 1; i <= numPages; i++) {
    const page = await pdfDoc.getPage(i);
    const viewport = page.getViewport({ scale: 1 });
    const textContent = await page.getTextContent();

    const pageItems = textContent.items
      .filter((item) => item.str && item.str.trim().length > 0)
      .map((item, idx) => ({
        str: item.str,
        transform: item.transform,
        width: item.width || 0,
        height: item.height || 0,
        itemIndex: idx,
      }));

    pages.push({ pageIndex: i - 1, viewport, items: pageItems });

    for (let j = 0; j < pageItems.length; j++) {
      const normalized = normalizeForMatch(pageItems[j].str);
      if (normalized.length === 0) continue;

      const separator = concatParts.length > 0 ? ' ' : '';
      indexMap.push({
        pageIdx: i - 1,
        itemIdx: j,
        charStart: charOffset + separator.length,
        charEnd: charOffset + separator.length + normalized.length,
      });
      concatParts.push(normalized);
      charOffset += separator.length + normalized.length;
    }
  }

  return { pages, concat: concatParts.join(' '), indexMap };
};

// =====================================================================
//  STOP WORDS
// =====================================================================

const STOP_WORDS = new Set([
  'a','an','the','of','in','to','for','and','or','is','are','was',
  'were','be','been','being','with','by','at','on','from','as','it',
  'its','this','that','these','those','we','they','he','she','which',
  'who','whom','but','not','no','all','each','has','have','had',
  'do','does','did','will','would','could','should','may','might',
  'can','than','also','into','about','such','between','through',
]);

// =====================================================================
//  MATCHING PRIMITIVES
// =====================================================================

const findSubstring = (haystack, probe, fromIdx = 0) => {
  if (!probe || !haystack) return -1;
  return haystack.indexOf(probe, fromIdx);
};

const buildProbesAtPosition = (words, startIdx) => {
  const probes = [];
  for (const size of [12, 8, 6, 4, 3]) {
    const end = Math.min(startIdx + size, words.length);
    if (end - startIdx < Math.min(size, 3)) continue;
    probes.push(words.slice(startIdx, end).join(' '));
  }
  return probes;
};

const slidingProbeForward = (pdfConcat, words, step = 4, maxPositions = 15) => {
  for (let pos = 0; pos < Math.min(words.length, step * maxPositions); pos += step) {
    for (const probe of buildProbesAtPosition(words, pos)) {
      const idx = findSubstring(pdfConcat, probe);
      if (idx !== -1) return idx;
    }
  }
  return -1;
};

const slidingProbeBackward = (pdfConcat, words, searchAfterIdx = 0, step = 4, maxPositions = 15) => {
  for (let offset = 0; offset < Math.min(words.length, step * maxPositions); offset += step) {
    const startIdx = Math.max(0, words.length - 12 - offset);
    for (const probe of buildProbesAtPosition(words, startIdx)) {
      const idx = findSubstring(pdfConcat, probe, searchAfterIdx);
      if (idx !== -1) return idx + probe.length;
    }
  }
  return -1;
};

const distinctiveWordFallback = (pdfConcat, words) => {
  const distinctive = words
    .filter((w) => !STOP_WORDS.has(w) && w.length > 4)
    .sort((a, b) => b.length - a.length)
    .slice(0, 5);
  for (const word of distinctive) {
    const idx = findSubstring(pdfConcat, word);
    if (idx !== -1) return idx;
  }
  return -1;
};

const charToItem = (indexMap, charPos) => {
  for (let i = 0; i < indexMap.length; i++) {
    if (charPos >= indexMap[i].charStart && charPos < indexMap[i].charEnd) return i;
  }
  if (indexMap.length > 0 && charPos >= indexMap[indexMap.length - 1].charEnd) {
    return indexMap.length - 1;
  }
  return -1;
};

// =====================================================================
//  SINGLE-TEXT MATCH — runs all phases for ONE text input
// =====================================================================

/**
 * Attempt to locate `rawText` inside the concatenated PDF string.
 * Returns { startCharIdx, endCharIdx } on success, or null.
 *
 * Phases:
 *   1. Direct substring (first/last 20 words)
 *   2. Sliding probe forward + backward
 *   3. Distinctive-word fallback (200-char window)
 */
const findMatchInPdf = (pdfConcat, rawText) => {
  const cleaned = cleanText(rawText);
  if (!cleaned) return null;

  const normalized = normalizeForMatch(cleaned);
  const words = normalized.split(' ').filter((w) => w.length > 0);
  if (words.length === 0) return null;

  let startCharIdx = -1;
  let endCharIdx = -1;

  // Phase 1: direct substring
  const headSize = Math.min(20, words.length);
  const headProbe = words.slice(0, headSize).join(' ');
  startCharIdx = findSubstring(pdfConcat, headProbe);

  if (startCharIdx !== -1) {
    const tailProbe = words.slice(Math.max(0, words.length - 20)).join(' ');
    const tailSearch = findSubstring(pdfConcat, tailProbe, startCharIdx);
    endCharIdx = tailSearch !== -1
      ? tailSearch + tailProbe.length
      : startCharIdx + headProbe.length;
  }

  // Phase 2: sliding probe
  if (startCharIdx === -1) {
    startCharIdx = slidingProbeForward(pdfConcat, words);
  }
  if (startCharIdx !== -1 && endCharIdx === -1) {
    endCharIdx = slidingProbeBackward(pdfConcat, words, startCharIdx);
    if (endCharIdx === -1 || endCharIdx <= startCharIdx) {
      endCharIdx = startCharIdx + Math.min(normalized.length, pdfConcat.length - startCharIdx);
    }
  }

  // Phase 3: distinctive word fallback
  if (startCharIdx === -1) {
    startCharIdx = distinctiveWordFallback(pdfConcat, words);
    if (startCharIdx !== -1) {
      endCharIdx = Math.min(startCharIdx + 200, pdfConcat.length);
    }
  }

  if (startCharIdx === -1) return null;

  return {
    startCharIdx,
    endCharIdx: Math.min(endCharIdx, pdfConcat.length),
  };
};

// =====================================================================
//  PRIORITY-BASED HIGHLIGHT RANGE  (text1 → text2 → text3)
// =====================================================================

/**
 * Try each text in priority order. Return the first successful match.
 *
 * @param {object}   pdfTextData  - output of extractPdfText
 * @param {string[]} texts        - [text1, text2, text3] in priority order
 * @returns {{ items, startPage, matchedIndex }} | null
 *          matchedIndex: which text[] entry matched (0, 1, or 2)
 */
const findHighlightRange = (pdfTextData, texts) => {
  const { concat: pdfConcat, indexMap } = pdfTextData;
  if (!pdfConcat || indexMap.length === 0) return null;

  for (let ti = 0; ti < texts.length; ti++) {
    const text = texts[ti];
    if (!text) continue;

    const match = findMatchInPdf(pdfConcat, text);
    if (!match) continue;

    const startMapIdx = charToItem(indexMap, match.startCharIdx);
    const endMapIdx = charToItem(indexMap, match.endCharIdx - 1);
    if (startMapIdx === -1) continue;

    const effectiveEnd = endMapIdx !== -1 ? endMapIdx : startMapIdx;
    const items = [];
    for (let i = startMapIdx; i <= effectiveEnd; i++) {
      items.push({ pageIdx: indexMap[i].pageIdx, itemIdx: indexMap[i].itemIdx });
    }
    if (items.length === 0) continue;

    const uniquePages = [...new Set(items.map((it) => it.pageIdx))];
    console.log(
      `PdfHighlightViewer: matched text${ti + 1} → ${items.length} items on page(s) [${uniquePages.join(', ')}]`
    );

    return { items, startPage: items[0].pageIdx, matchedIndex: ti };
  }

  console.log('PdfHighlightViewer: no match found across all text inputs');
  return null;
};

// =====================================================================
//  HIGHLIGHT RECT COMPUTATION
// =====================================================================

const computeHighlightRects = (pdfTextData, highlightItems) => {
  const rectsByPage = new Map();

  for (const { pageIdx, itemIdx } of highlightItems) {
    const page = pdfTextData.pages[pageIdx];
    if (!page) continue;
    const item = page.items[itemIdx];
    if (!item) continue;

    const t = item.transform;
    const fontHeight = Math.max(Math.abs(t[0]), Math.abs(t[3])) || 12;
    const pageHeight = page.viewport.height;

    const left = t[4];
    const top = pageHeight - t[5] - fontHeight * 0.85;
    const width = item.width || fontHeight * 0.6 * (item.str?.length || 1);
    const height = fontHeight * 1.15;

    if (!rectsByPage.has(pageIdx)) rectsByPage.set(pageIdx, []);
    rectsByPage.get(pageIdx).push({ left, top, width, height });
  }

  return rectsByPage;
};

// =====================================================================
//  PAGE CONTAINER DETECTION
// =====================================================================

const getPageContainer = (rootContainer, pageIdx) => {
  const byTestId = rootContainer.querySelector(
    `[data-testid="core__page-layer-${pageIdx}"]`
  );
  if (byTestId) return byTestId;

  const innerPage = rootContainer.querySelector(
    `[data-testid="core__inner-page-${pageIdx}"]`
  );
  if (innerPage) {
    return innerPage.querySelector('.rpv-core__page-layer') || innerPage;
  }

  const allLayers = rootContainer.querySelectorAll('.rpv-core__page-layer');
  return allLayers[pageIdx] || null;
};

// =====================================================================
//  DOM HIGHLIGHT INJECTION
// =====================================================================

const OVERLAY_CLASS = 'pdf-highlight-overlay';
const RECT_CLASS = 'pdf-highlight-rect';

const clearHighlightOverlays = (container) => {
  if (!container) return;
  container.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((el) => el.remove());
};

const injectHighlightOverlays = (container, rectsByPage, pdfTextData) => {
  if (!container || !rectsByPage || rectsByPage.size === 0) return;

  rectsByPage.forEach((rects, pageIdx) => {
    const pageContainer = getPageContainer(container, pageIdx);
    if (!pageContainer) {
      console.warn(`PdfHighlightViewer: page container not found for page ${pageIdx}`);
      return;
    }

    const page = pdfTextData.pages[pageIdx];
    if (!page) return;

    const renderedWidth = pageContainer.clientWidth;
    const pdfWidth = page.viewport.width;
    if (!renderedWidth || !pdfWidth) return;
    const scale = renderedWidth / pdfWidth;

    if (getComputedStyle(pageContainer).position === 'static') {
      pageContainer.style.position = 'relative';
    }

    // Remove existing overlay on THIS page only
    pageContainer
      .querySelectorAll(`.${OVERLAY_CLASS}[data-highlight-page="${pageIdx}"]`)
      .forEach((el) => el.remove());

    const overlay = document.createElement('div');
    overlay.className = OVERLAY_CLASS;
    overlay.setAttribute('data-highlight-page', String(pageIdx));
    overlay.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:1;';

    for (const rect of rects) {
      const div = document.createElement('div');
      div.className = RECT_CLASS;
      div.style.cssText =
        `position:absolute;` +
        `left:${rect.left * scale}px;` +
        `top:${rect.top * scale}px;` +
        `width:${rect.width * scale}px;` +
        `height:${rect.height * scale}px;` +
        `background:rgba(255,213,79,0.4);` +
        `border-radius:2px;` +
        `mix-blend-mode:multiply;`;
      overlay.appendChild(div);
    }

    pageContainer.appendChild(overlay);
  });
};

/**
 * scrollToHighlight — polls until the target page container exists in the
 * DOM, then scrolls it into view.  Returns a cancel function so the caller
 * can abort pending retries on unmount / re-render.
 *
 * When the page is far from the initial viewport and the Viewer hasn't
 * lazily rendered it yet, we first nudge the scroll container towards
 * the estimated position of that page — this triggers the Viewer to
 * mount the page layer, at which point the next poll picks it up.
 */
const scrollToHighlight = (container, pageIdx) => {
  if (!container) return () => {};

  const MAX_ATTEMPTS = 25;
  const POLL_INTERVAL = 200; // ms between retries
  let attempt = 0;
  let timerId = null;
  let cancelled = false;

  /**
   * Find the inner element that actually scrolls (the Viewer creates its
   * own scrollable wrapper inside our container div).  Fall back to the
   * container itself.
   */
  const getScrollParent = () => {
    const inner = container.querySelector('.rpv-core__inner-pages');
    return inner || container;
  };

  /**
   * Estimate the vertical offset of `pageIdx` inside the scroll parent
   * by looking at the heights of pages already in the DOM.  If no pages
   * are rendered yet we can't estimate — return null.
   */
  const estimatePageOffset = (scrollParent) => {
    const allPages = scrollParent.querySelectorAll('.rpv-core__page-layer');
    if (allPages.length === 0) return null;

    // Use the first rendered page's height as a representative
    const samplePage = allPages[0];
    const pageHeight = samplePage.offsetHeight || samplePage.clientHeight;
    if (!pageHeight) return null;

    // Account for any gap/margin between pages
    let gap = 0;
    if (allPages.length > 1) {
      const first = allPages[0].getBoundingClientRect();
      const second = allPages[1].getBoundingClientRect();
      gap = second.top - first.bottom;
      if (gap < 0) gap = 0;
    }

    return pageIdx * (pageHeight + gap);
  };

  const tryScroll = () => {
    if (cancelled) return;

    const target = getPageContainer(container, pageIdx);

    if (target) {
      // Page exists — scroll to it.  Use the actual scroll parent so we
      // don't accidentally shift an outer modal/body.
      const scrollParent = getScrollParent();
      const parentRect = scrollParent.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const offset = targetRect.top - parentRect.top + scrollParent.scrollTop;

      scrollParent.scrollTo({ top: offset, behavior: 'smooth' });
      return; // done
    }

    attempt++;

    if (attempt < MAX_ATTEMPTS) {
      // Page not in the DOM yet — nudge the scroll container towards the
      // estimated position so the Viewer lazily renders that page.
      if (attempt % 3 === 1) {
        const scrollParent = getScrollParent();
        const estimated = estimatePageOffset(scrollParent);
        if (estimated !== null) {
          scrollParent.scrollTop = estimated;
        }
      }

      timerId = setTimeout(tryScroll, POLL_INTERVAL);
    }
  };

  // Kick off the first attempt after a short delay to let the Viewer
  // render at least the initial batch of pages.
  timerId = setTimeout(tryScroll, 150);

  // Return a cancel handle for cleanup
  return () => {
    cancelled = true;
    clearTimeout(timerId);
  };
};

// =====================================================================
//  TOOLBAR STYLES
// =====================================================================

const toolbarStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 10px',
  borderBottom: '1px solid #e0e4ea',
  background: '#f8f9fb',
  borderRadius: '4px 4px 0 0',
  flexShrink: 0,
};

const btnGroupStyle = { display: 'flex', alignItems: 'center', gap: '4px' };

const toolbarBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #d0d5dd',
  background: '#fff',
  borderRadius: '4px',
  padding: '4px 8px',
  cursor: 'pointer',
  fontSize: '13px',
  color: '#344054',
  lineHeight: 1,
  minWidth: '28px',
  height: '28px',
};

const scaleLabelStyle = {
  fontSize: '12px',
  color: '#667085',
  minWidth: '42px',
  textAlign: 'center',
  userSelect: 'none',
  cursor: 'pointer',
};

// =====================================================================
//  COMPONENT
// =====================================================================

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const DEFAULT_ZOOM_IDX = 2;

/**
 * PdfHighlightViewer
 *
 * Generic PDF viewer that highlights the first matching text from a
 * prioritised list: text1 → text2 → text3.
 *
 * Props:
 *   pdfUrl  — presigned S3 / direct URL to the PDF
 *   text1   — highest priority search text  (e.g. table caption)
 *   text2   — medium priority               (e.g. verbatim phrase)
 *   text3   — lowest priority / broadest     (e.g. full chunk content)
 */
const PdfHighlightViewer = ({ pdfUrl, text1, text2, text3 }) => {
  const containerRef = useRef(null);
  const [highlightState, setHighlightState] = useState(null);
  const [zoomIdx, setZoomIdx] = useState(DEFAULT_ZOOM_IDX);

  // Cache extracted text so zoom changes don't re-extract
  const pdfTextCacheRef = useRef(null);

  const currentScale = ZOOM_STEPS[zoomIdx];

  const handleDocumentLoad = useCallback(
    async (e) => {
      const pdfDoc = e.doc;
      try {
        if (!pdfTextCacheRef.current) {
          pdfTextCacheRef.current = await extractPdfText(pdfDoc);
        }
        const pdfTextData = pdfTextCacheRef.current;

        // Priority cascade: text1 → text2 → text3
        const texts = [text1, text2, text3].filter(Boolean);
        if (texts.length === 0) return;

        const range = findHighlightRange(pdfTextData, texts);
        if (!range) {
          console.log('PdfHighlightViewer: no highlight range found');
          return;
        }

        const rectsByPage = computeHighlightRects(pdfTextData, range.items);
        setHighlightState({ rectsByPage, startPage: range.startPage, pdfTextData });
      } catch (err) {
        console.error('PdfHighlightViewer: error during highlight pipeline', err);
      }
    },
    [text1, text2, text3],
  );

  // Inject overlays after pages render
  useEffect(() => {
    if (!highlightState || !containerRef.current) return;

    const container = containerRef.current;
    const { rectsByPage, startPage, pdfTextData } = highlightState;

    const doInject = () => {
      clearHighlightOverlays(container);
      injectHighlightOverlays(container, rectsByPage, pdfTextData);
    };

    doInject();
    const cancelScroll = scrollToHighlight(container, startPage);

    // MutationObserver for re-renders (zoom, lazy page load)
    let isInjecting = false;
    let debounceTimer = null;

    const observer = new MutationObserver((mutations) => {
      if (isInjecting) return;

      const isOwnMutation = mutations.every((m) => {
        const nodes = [...Array.from(m.addedNodes || []), ...Array.from(m.removedNodes || [])];
        return nodes.length > 0 && nodes.every(
          (n) => n.nodeType === 1 && n.classList && n.classList.contains(OVERLAY_CLASS),
        );
      });
      if (isOwnMutation) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        isInjecting = true;
        doInject();
        requestAnimationFrame(() => { isInjecting = false; });
      }, 250);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => {
      cancelScroll();
      observer.disconnect();
      clearTimeout(debounceTimer);
      clearHighlightOverlays(container);
    };
  }, [highlightState, zoomIdx]);

  // Toolbar handlers
  const handleZoomIn = () => setZoomIdx((p) => Math.min(p + 1, ZOOM_STEPS.length - 1));
  const handleZoomOut = () => setZoomIdx((p) => Math.max(p - 1, 0));
  const handleZoomReset = () => setZoomIdx(DEFAULT_ZOOM_IDX);
  const handleDownload = () => pdfUrl && window.open(pdfUrl, '_blank', 'noopener,noreferrer');

  return (
    <Worker workerUrl={PDF_WORKER_URL}>
      {/* Toolbar */}
      <div style={toolbarStyle}>
        <div style={btnGroupStyle}>
          <button
            style={{ ...toolbarBtnStyle, opacity: zoomIdx <= 0 ? 0.4 : 1 }}
            onClick={handleZoomOut}
            disabled={zoomIdx <= 0}
            title="Zoom Out"
          >
            <i className="bi bi-dash"></i>
          </button>
          <span style={scaleLabelStyle} onClick={handleZoomReset} title="Reset Zoom">
            {Math.round(currentScale * 100)}%
          </span>
          <button
            style={{ ...toolbarBtnStyle, opacity: zoomIdx >= ZOOM_STEPS.length - 1 ? 0.4 : 1 }}
            onClick={handleZoomIn}
            disabled={zoomIdx >= ZOOM_STEPS.length - 1}
            title="Zoom In"
          >
            <i className="bi bi-plus"></i>
          </button>
        </div>
        <div style={btnGroupStyle}>
          <button style={toolbarBtnStyle} onClick={handleDownload} title="Open in New Tab">
            <i className="bi bi-download"></i>
          </button>
        </div>
      </div>

      {/* PDF Viewer — key forces re-mount on zoom so defaultScale applies */}
      <div ref={containerRef} style={{ height: 'calc(100% - 41px)', overflow: 'auto' }}>
        <Viewer
          key={`pdf-zoom-${currentScale}`}
          fileUrl={pdfUrl}
          defaultScale={currentScale}
          onDocumentLoad={handleDocumentLoad}
        />
      </div>
    </Worker>
  );
};

export default PdfHighlightViewer;